import axios from 'axios';

const M = '/api/method';

const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: base,
  timeout: 30000,
});

// Add request interceptor to include auth token if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers['X-ESIM-Admin-Auth'] = `Bearer ${token}`;
    }
    // JSON body for Frappe is fine
    if (config.method !== 'get' && config.headers) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Basic error normalization
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    // Treat 401/403/417 as authentication/authorization failures
    if (status === 401 || status === 403 || status === 417) {
      try {
        // Mark flag so Login page can show a clear message and optionally restore route
        sessionStorage.setItem('auth_expired', '1');
        try {
          const currentPath = window.location.pathname + window.location.search + window.location.hash;
          sessionStorage.setItem('auth_expired_path', currentPath);
        } catch {}
      } catch {}

      // Clear auth token and user
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Notify app to logout/react
      window.dispatchEvent(new CustomEvent('auth-expired'));

      // Normalize message
      return Promise.reject(new Error('Authentication expired. Please log in again.'));
    }

    if (error?.response?.data?.exception || error?.response?.data?._error_message) {
      const msg = error.response.data._error_message || error.response.data.exception;
      return Promise.reject(new Error(msg));
    }
    return Promise.reject(error);
  }
);

export type OrdersReportFilters = {
  order_id?: string;
  user_email?: string;
  package_name?: string;
  price?: number;
  payment_type?: ('MaMs' | 'Card' | 'Free' | 'Support') | ('MaMs' | 'Card' | 'Free' | 'Support')[];
  order_type?: ('Top-up' | 'e-sim purchase') | ('Top-up' | 'e-sim purchase')[];
  date_created_from?: string;
  date_created_to?: string;
  date_activated_from?: string;
  date_activated_to?: string;
  status?: ('Requested' | 'Pending' | 'Payment Pending' | 'Active' | 'Failed' | 'Canceled') | ('Requested' | 'Pending' | 'Payment Pending' | 'Active' | 'Failed' | 'Canceled')[];
};

export type EsimsReportFilters = {
  user_email?: string;
  iccid?: string;
  status?: ('Requested' | 'Pending' | 'Active' | 'Suspended' | 'Failed' | 'Canceled') | ('Requested' | 'Pending' | 'Active' | 'Suspended' | 'Failed' | 'Canceled')[];
  activated_from?: string;
  activated_to?: string;
  last_topup_from?: string;
  last_topup_to?: string;
};

export async function login(email: string, password: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.login`, { email, password });
  return data.message || data;
}

export async function health() {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.health`);
  return data.message || data;
}

export async function ordersReport(filters: OrdersReportFilters, page = 1, pageSize = 50, searchMode: 'AND' | 'OR' = 'AND') {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.orders_report`, {
    filters_json: JSON.stringify(filters),
    page,
    page_size: pageSize,
    search_mode: searchMode
  });
  return data.message || data;
}

export async function esimsReport(filters: EsimsReportFilters, page = 1, pageSize = 50, searchMode: 'AND' | 'OR' = 'AND') {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.esims_report`, {
    filters_json: JSON.stringify(filters),
    page,
    page_size: pageSize,
    search_mode: searchMode
  });
  return data.message || data;
}

export async function topup(iccid: string, item_code: string, qty = 1) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.topup`, { iccid, item_code, qty });
  return data.message || data;
}

export async function listPackages(status?: string | string[], product_type?: string | string[], q?: string, page = 1, pageSize = 50, topup = false) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.list_packages`, { 
    status, 
    product_type,
    q, 
    page, 
    page_size: pageSize, 
    topup 
  });
  return data.message || data;
}

export async function updatePackagePrices(payload: {
  product: string;
  gross_usd?: number;
  gross_eur?: number;
  gross_gbp?: number;
  gross_mams?: number;
  status?: 'Active' | 'Hidden';
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.update_package_prices`, payload);
  return data.message || data;
}

export async function revertPackagePriceToProvider(product: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.revert_package_price_to_provider`, { product });
  return data.message || data;
}

export async function setPackageStatus(product: string, status: 'Active' | 'Hidden') {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.set_package_status`, { product, status });
  return data.message || data;
}

export async function setPackageType(product: string, product_type: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.set_package_type`, { product, product_type });
  return data.message || data;
}

export async function searchEsim(query: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.search_esim`, { query });
  return data.message || data;
}

export async function userProfile(email: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.user_profile`, { email });
  return data.message || data;
}

// Support Agents (Admin only)
export async function listEsimAdminUsers(q?: string, page = 1, pageSize = 50) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.list_esim_admin_users`, { 
    q, 
    page, 
    page_size: pageSize 
  });
  return data.message || data;
}

export async function listSupportAgents(q?: string, page = 1, pageSize = 50) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.list_support_agents`, { 
    q, 
    page, 
    page_size: pageSize 
  });
  return data.message || data;
}

export async function createEsimAdminUser(payload: { 
  email: string; 
  password: string; 
  status?: 'Active' | 'Disabled';
  role: 'Administrator' | 'Support Agent';
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.create_esim_admin_user`, payload);
  return data.message || data;
}

export async function updateEsimAdminUser(payload: { 
  name: string; 
  password?: string; 
  status?: 'Active' | 'Disabled';
  role?: 'Administrator' | 'Support Agent';
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.update_esim_admin_user`, payload);
  return data.message || data;
}

export async function deleteEsimAdminUser(name: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.delete_esim_admin_user`, { name });
  return data.message || data;
}


export async function getTopupPackages(iccid: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.esim_admin.get_topup_packages?iccid=${iccid}`);
  return data.message || data;
}

export async function getEsimCustomers(q?: string, page = 1, pageSize = 50) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.get_esim_customers`, { 
    q, 
    page, 
    page_size: pageSize 
  });
  return data.message || data;
}

export async function clearCurrencyCache() {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin.clear_currency_cache`);
  return data.message || data;
}

// Background Jobs API
export async function jobsDashboard(jobCode: string = 'drivers-auto-renew') {
  const { data } = await api.get(`${M}/ski_delivery.api.esim_admin_jobs.jobs_dashboard`, {
    params: { job_code: jobCode }
  });
  return data.message || data;
}

export async function jobsFailedList(jobCode: string = 'drivers-auto-renew', page: number = 1, pageSize: number = 25) {
  const { data } = await api.get(`${M}/ski_delivery.api.esim_admin_jobs.jobs_failed_list`, {
    params: { job_code: jobCode, page, page_size: pageSize }
  });
  return data.message || data;
}

export async function jobsRetryFailure(failureName: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin_jobs.jobs_retry_failure`, {
    failure_name: failureName
  });
  return data.message || data;
}

export async function jobsRetryRun(jobCode: string = 'drivers-auto-renew') {
  const { data } = await api.post(`${M}/ski_delivery.api.esim_admin_jobs.jobs_retry_run`, {
    job_code: jobCode
  });
  return data.message || data;
}
